import React, { useState, useMemo } from "react";
import { withRouter } from "react-router-dom";
import queries from "../helpers/queries";
import { useForm } from "react-hook-form";
import { withUser } from "../Contexts/user";
import { FaFileDownload } from "react-icons/fa";
import { IoIosAdd } from "react-icons/io";
import Table from "../Components/darkBasicTable";
import createPdf from "../Components/createPdf";
import multiPdf from "../Components/multiPdf";
import moment from "moment";
import { useQuery } from "@apollo/react-hooks";
import useDimensions from "../Hooks/useDimensions";
import Select from "react-select";

const statusOptions = [
    {
        label: "All",
        value: "all",
    },
    {
        label: "Valid",
        value: "valid",
    },
    {
        label: "Void",
        value: "void",
    },
];

/** Matches `override.css` @media (max-width: 768px) — filters row is desktop above this. */
const DESKTOP_FILTERS_MIN_WIDTH = 769;

const PaymentVouchers = withRouter(() => {
    const size = useDimensions();
    const [showDetails, setShowDetails] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const isDesktopFilters = (size.width ?? 0) >= DESKTOP_FILTERS_MIN_WIDTH;

    const filterSelectStyles = useMemo(
        () => ({
            container: (base) => ({
                ...base,
                ...(isDesktopFilters
                    ? { height: "100%", flex: 1, minHeight: 0 }
                    : { width: "100%", maxWidth: "100%" }),
            }),
            control: (base) => ({
                ...base,
                borderRadius: "8px",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                ...(isDesktopFilters
                    ? {
                          minWidth: "100%",
                          width: "100%",
                          minHeight: "44px",
                          height: "100%",
                          boxSizing: "border-box",
                          alignItems: "center",
                      }
                    : {}),
            }),
            valueContainer: (base) => ({
                ...base,
                ...(isDesktopFilters ? { padding: "0 0.5rem" } : {}),
            }),
            indicatorsContainer: (base) => ({
                ...base,
                ...(isDesktopFilters ? { height: "100%" } : {}),
            }),
        }),
        [isDesktopFilters]
    );

    useForm({
        defaultValues: {
            payee: "",
            method: undefined,
            date: "",
            batch: "",
            name: "",
            nickname: "",
            amount: "",
            status: "valid",
        },
    });

    useForm({
        defaultValues: {
            status: {
                label: "Valid",
                value: "valid",
            },
        },
    });

    const [filters, setFilters] = useState({
        dateTo: moment(Date.now()).format("YYYY-MM-DD"),
        dateFrom: moment(Date.now()).subtract(1, "months").format("YYYY-MM-DD"),
        calculatePoints: statusOptions[0],
    });

    const columns = useMemo(() => {
        const NumberFormatter = new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return [
            {
                Header: "Agent",
                accessor: "payee",
                Cell: ({ row: { original } }) => (
                    <div className="capitalize">
                        {original.payee.nickname != '-' ? `${original.payee.nickname} ` : ''}{original.payee.name}
                    </div>
                ),
            },
            {
                Header: "Method",
                accessor: "method",
                Cell: ({ value }) => (
                    <div className="capitalize">{value}</div>
                ),
            },
            {
                Header: "Date",
                accessor: "date",
            },
            {
                Header: "Order No",
                accessor: "orderNo",
                Cell: ({ value }) => (
                    <div className="capitalize">{value}</div>
                ),
            },
            {
                Header: "Project",
                accessor: "unit",
                Cell: ({ row: { original } }) => (
                    <div>{original.item.address}</div>
                ),
            },
            {
                Header: "Amount",
                id: "amount",
                Cell: ({ row: { original } }) => (
                    <div>
                        RM{" "}
                        {NumberFormatter.format(
                            original.item.amount +
                                original.item2.amount +
                                original.item3.amount +
                                original.item4.amount +
                                original.item5.amount
                        )}
                    </div>
                ),
            },
            {
                Header: "",
                id: "actions",
                Cell: ({ row: { original } }) => {
                    return (
                        <div className="flex content-start items-center">
                            <div className="cursor-pointer flex justify-center">
                                <FaFileDownload
                                    onClick={() => createPdf(original)}
                                />
                            </div>
                        </div>
                    );
                },
            },
        ];
    }, []);

    const [paginationState, setPaginationState] = useState({
        limit: 9999,
        page: 1,
    });

    const { loading: paymentVouchersLoading, data: paymentVouchersData } =
        useQuery(queries.FETCH_USER_PAYMENT_VOUCHERS, {
            variables: {
                limit: paginationState.limit,
                page: paginationState.page,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                calculatePoints: filters.calculatePoints.value,
            },
            fetchPolicy: "network-only",
            onCompleted: ({ fetchUserPaymentVouchers: { pagination } }) => {
                setPaginationState(pagination);
                setIsLoading(false);
            },
        });

    const NumberFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return (
        <>
            {isLoading ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                </div>
            ) : (
                <div className="my-10 lg:my-0 lg:p-8">
                    {!showDetails ? (
                        // Summary Card
                        <div className="cases-summary-card">
                            <h1 className="cases-title">PAYMENT VOUCHERS</h1>
                            <div className="cases-grid">
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Total Vouchers</div>
                                    <div className="cases-stat-value">
                                        {paymentVouchersData?.fetchUserPaymentVouchers?.totals?.totalPaymentVoucher || "0"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Total Amount</div>
                                    <div className="cases-stat-value">
                                        RM {paymentVouchersData?.fetchUserPaymentVouchers?.totals?.totalVoucherAmount 
                                            ? NumberFormatter.format(paymentVouchersData.fetchUserPaymentVouchers.totals.totalVoucherAmount)
                                            : "0.00"}
                                    </div>
                                </div>
                            </div>
                            <button 
                                className="cases-view-details"
                                onClick={() => setShowDetails(true)}
                            >
                                VIEW DETAILS
                            </button>
                        </div>
                    ) : (
                        // Details Card
                        <div className="cases-details-card">
                            <div className="cases-details-header">
                                <h2 className="cases-details-title">PAYMENT VOUCHER DETAILS</h2>
                                <button 
                                    className="cases-back-btn"
                                    onClick={() => setShowDetails(false)}
                                >
                                    Back to Summary
                                </button>
                            </div>

                            <div className="cases-filters">
                                <input
                                    type="date"
                                    className="cases-filter-select"
                                    value={filters.dateFrom}
                                    onChange={(e) => {
                                        setIsLoading(true);
                                        setFilters((prev) => ({
                                            ...prev,
                                            dateFrom: e.target.value,
                                        }));
                                        setPaginationState((prev) => ({
                                            ...prev,
                                            page: 1,
                                        }));
                                    }}
                                />
                                <input
                                    type="date"
                                    className="cases-filter-select"
                                    value={filters.dateTo}
                                    onChange={(e) => {
                                        setIsLoading(true);
                                        setFilters((prev) => ({
                                            ...prev,
                                            dateTo: e.target.value,
                                        }));
                                        setPaginationState((prev) => ({
                                            ...prev,
                                            page: 1,
                                        }));
                                    }}
                                />
                                <div
                                    style={
                                        isDesktopFilters
                                            ? {
                                                  width: 180,
                                                  flexShrink: 0,
                                                  alignSelf: "stretch",
                                                  display: "flex",
                                                  flexDirection: "column",
                                              }
                                            : { width: "100%", maxWidth: "100%" }
                                    }
                                >
                                    <Select
                                        styles={filterSelectStyles}
                                        options={statusOptions}
                                        value={filters.calculatePoints}
                                        placeholder="Calculate Points"
                                        onChange={(val) => {
                                            setIsLoading(true);
                                            setPaginationState((prev) => ({
                                                ...prev,
                                                page: 1,
                                            }));
                                            setFilters((prev) => ({
                                                ...prev,
                                                calculatePoints: val,
                                            }));
                                        }}
                                    />
                                </div>
                                {paymentVouchersData && (
                                    <button
                                        className="cases-search-btn"
                                        onClick={() => multiPdf(paymentVouchersData.fetchUserPaymentVouchers.data)}
                                    >
                                        <IoIosAdd className="inline" /> Download All
                                    </button>
                                )}
                            </div>

                            <div className="cases-table-container">
                                <Table
                                    columns={columns}
                                    data={paymentVouchersData?.fetchUserPaymentVouchers?.data || []}
                                    updatePagination={setPaginationState}
                                    loading={paymentVouchersLoading}
                                    pageCount={paymentVouchersData?.fetchUserPaymentVouchers?.pagination?.totalPages || 0}
                                    totalCount={paymentVouchersData?.fetchUserPaymentVouchers?.pagination?.documents || 0}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
});

export default withUser(PaymentVouchers);
